import { CountryLanguageModel } from '@_types/organisms';
import { openSitePreferenceEvent } from '@helpers/TopBar';
import DropdownField from '@molecules/dropdown-field/DropdownField';
import { StaticLanguage } from '@organisms/header/SitePreferences';

import getComponentMarkup from './getComponentMarkup';

const overflowCssPropertyName = '--body-overflow-y-by-site-preference';

class SitePreferences {
  sitePreferences: HTMLDivElement;
  closeIcon?: HTMLDivElement | null;
  countryCode?: string;
  initCountryCode?: string;
  languageCode?: string;
  header?: HTMLDivElement | null;
  dataCountryLanguage?: CountryLanguageModel[];
  submitButton?: HTMLAnchorElement | null;
  preferenceForm: HTMLFormElement | null;

  constructor(sitePreferences: HTMLDivElement) {
    this.sitePreferences = sitePreferences;
    this.closeIcon = this.sitePreferences.querySelector<HTMLDivElement>('.kf-o-site-preferences__close-icon');
    this.header = document.querySelector<HTMLDivElement>('.kf-o-header');
    this.submitButton = this.sitePreferences.querySelector<HTMLAnchorElement>('.kf-o-site-preferences__form-button');
    this.preferenceForm = this.sitePreferences.querySelector<HTMLFormElement>('.kf-o-site-preferences__form');
  }

  init() {
    this.addEventCloseElement();
    this.addEventOpenElement();
    this.setupCountry();
    this.initCountryLanguage();

    this.getInitCurrentCountry();

    this.initCountry();
    this.handleOutsideClick();
  }

  initCountryLanguage() {
    const countryLanguageEl = this.sitePreferences.querySelector<HTMLDivElement>('[data-country-language]');

    this.dataCountryLanguage = JSON.parse(countryLanguageEl?.dataset.countryLanguage || '[]');
  }

  getInitCurrentCountry() {
    const countryOptions = this.sitePreferences.querySelectorAll<HTMLInputElement>(
      '.kf-m-dropdown-field[data-name="country"] .kf-a-dropdown__options input'
    );

    if (!countryOptions?.length) return;

    this.countryCode = [...countryOptions].find((option) => option.defaultChecked)?.value;
    this.initCountryCode = this.countryCode;
  }

  handleOutsideClick() {
    const container = this.sitePreferences.querySelector<HTMLElement>('.kf-o-site-preferences__container');

    container?.onOutsideClick(() => {
      this.handleCloseElement();
    });
  }

  handleLanguagesByCountry() {
    const languageWrapper = this.sitePreferences.querySelector<HTMLDivElement>('.kf-o-site-preferences__language');

    if (!languageWrapper) return;

    const targetCountry = this.dataCountryLanguage?.find((country) => country.countryCode === this.countryCode);

    if (targetCountry?.languages) {
      if (targetCountry.languages.length > 1) {
        const languages = JSON.parse(languageWrapper?.dataset.languages || '{}');
        const languageLabel = languageWrapper?.dataset.languageLabel || 'Language';

        const html = getComponentMarkup(DropdownField, {
          dropdown: {
            ...languages,
            options: targetCountry.languages.map((item, index) => ({ selected: !index, text: item.languageName, value: item.languageCode })),
            dataSet: { tabIndex: 0 },
          },
          title: languageLabel,
        });

        if (languageWrapper) {
          languageWrapper.innerHTML = html;
        }

        window.KFDropdown.init();
        const dropdown = languageWrapper.querySelector<HTMLDivElement>('.kf-a-dropdown');

        this.onCountryChange();
        dropdown?.dispatchEvent(new CustomEvent('reset'));
      } else {
        const staticLanguageLabel = languageWrapper?.dataset.staticLanguageLabel || 'Language';
        const html = getComponentMarkup(StaticLanguage, {
          languages: targetCountry.languages,
          staticLanguageLabel,
        });

        if (languageWrapper) {
          languageWrapper.innerHTML = html;
        }

        this.languageCode = targetCountry.languages[0].languageCode;
      }
    }
  }

  setupCountry() {
    const countryDropdown = this.sitePreferences.querySelector<HTMLDivElement>('.kf-m-dropdown-field[data-name="country"] .kf-a-dropdown');
    const languageDropdown = this.sitePreferences.querySelector<HTMLDivElement>('.kf-m-dropdown-field[data-name="language"] .kf-a-dropdown');

    languageDropdown?.dispatchEvent(new CustomEvent('reset'));
    this.handleLanguagesByCountry();

    countryDropdown?.addEventListener('dropdown-change', (event) => {
      this.countryCode = event.detail?.value;
      languageDropdown?.dispatchEvent(new CustomEvent('reset'));

      this.handleLanguagesByCountry();

      this.handleChangeSitePreference();
    });
    languageDropdown?.addEventListener('dropdown-change', (event) => {
      this.languageCode = event.detail?.value;
      this.handleChangeSitePreference();
    });
  }

  onCountryChange() {
    const languageDropdown = this.sitePreferences.querySelector<HTMLDivElement>('.kf-m-dropdown-field[data-name="language"] .kf-a-dropdown');

    languageDropdown?.addEventListener('dropdown-change', (event) => {
      this.languageCode = event.detail?.value;
      this.handleChangeSitePreference();
    });
  }

  initCountry() {
    const languageDropdown = this.sitePreferences.querySelector<HTMLDivElement>('.kf-m-dropdown-field[data-name="language"] .kf-a-dropdown');

    languageDropdown?.dispatchEvent(new CustomEvent('reset'));
    this.handleLanguagesByCountry();
  }

  handleChangeSitePreference() {
    const targetCountry = this.dataCountryLanguage?.find((country) => country.countryCode === this.countryCode);
    const target = targetCountry?.languages?.find((language) => language.languageCode === this.languageCode);

    if (target && this.submitButton) {
      this.submitButton.href = target.targetLink;
      this.submitButton.classList.remove('disabled');
    } else if (this.submitButton) {
      this.submitButton.classList.add('disabled');
      this.submitButton.removeAttribute('href');
    }
  }

  addEventOpenElement() {
    window.addEventListener(openSitePreferenceEvent, this.handleOpenElement.bind(this));
  }

  handleOpenElement() {
    this.sitePreferences.classList.remove('hidden');
    this.sitePreferences.querySelector<HTMLDivElement>('.kf-a-dropdown__label')?.focus();
    const countryDropdown = this.sitePreferences.querySelector<HTMLDivElement>('.kf-m-dropdown-field[data-name="country"] .kf-a-dropdown');

    countryDropdown?.dispatchEvent(new CustomEvent('reset', { detail: { value: this.initCountryCode } }));

    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.setProperty(overflowCssPropertyName, 'hidden');
    document.body.style.paddingRight = `${scrollBarWidth}px`;

    if (this.header && this.header.computedStyleMap) {
      const position = this.header.computedStyleMap().get('position');

      if (position instanceof CSSKeywordValue && position.value === 'absolute') {
        this.header.style.paddingRight = `${scrollBarWidth}px`;
      }

      this.header.classList.add('kf-o-header--site-preferences-opened');
    }
  }

  handleCloseElement() {
    this.sitePreferences.classList.add('hidden');
    document.body.style.removeProperty(overflowCssPropertyName);
    document.body.style.paddingRight = '';
    if (this.header && this.header.computedStyleMap) {
      const position = this.header.computedStyleMap().get('position');

      if (position instanceof CSSKeywordValue && position.value === 'absolute') {
        this.header.style.paddingRight = '';
      }
      this.header.classList.remove('kf-o-header--site-preferences-opened');
    }
  }

  addEventCloseElement() {
    this.closeIcon?.addEventListener('click', this.handleCloseElement.bind(this));

    this.closeIcon?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        this.handleCloseElement();
      }
    });
  }
}

export { SitePreferences };
